import type { PathKind, Point } from "../domain/drill";

type Sample = Point & { distance: number; tx: number; ty: number };

function distance(a: Point, b: Point) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function trimPathEnd(points: Point[], amount: number): Point[] {
  if (points.length < 2 || amount <= 0) return points;
  const trimmed = points.map((point) => ({ ...point }));
  let remaining = amount;
  while (trimmed.length > 1 && remaining > 0) {
    const end = trimmed.at(-1)!;
    const before = trimmed.at(-2)!;
    const segmentLength = distance(before, end);
    if (segmentLength <= remaining) {
      trimmed.pop();
      remaining -= segmentLength;
      continue;
    }
    const ratio = remaining / segmentLength;
    trimmed[trimmed.length - 1] = {
      x: end.x + (before.x - end.x) * ratio,
      y: end.y + (before.y - end.y) * ratio,
    };
    remaining = 0;
  }
  return trimmed;
}

export function samplePath(points: Point[], spacing = 4): Sample[] {
  if (points.length < 2) return [];
  // Pointer events arrive at uneven intervals. A short weighted moving average
  // removes hand jitter while preserving the start and finish selected by the coach.
  const smoothed = points.map((point, index) => {
    if (index === 0 || index === points.length - 1) return point;
    const before = points[index - 1];
    const after = points[index + 1];
    return {
      x: before.x * 0.2 + point.x * 0.6 + after.x * 0.2,
      y: before.y * 0.2 + point.y * 0.6 + after.y * 0.2,
    };
  });
  const segments = smoothed.slice(1).map((point, index) => ({
    from: smoothed[index],
    to: point,
    length: distance(smoothed[index], point),
  }));
  const total = segments.reduce((sum, segment) => sum + segment.length, 0);
  if (!total) return [];

  const samples: Sample[] = [];
  let segmentIndex = 0;
  let traversed = 0;
  for (let travelled = 0; travelled <= total; travelled += spacing) {
    while (
      segmentIndex < segments.length - 1 &&
      travelled > traversed + segments[segmentIndex].length
    ) {
      traversed += segments[segmentIndex].length;
      segmentIndex += 1;
    }
    const segment = segments[segmentIndex];
    const ratio = segment.length
      ? Math.min(1, (travelled - traversed) / segment.length)
      : 0;
    const tx = (segment.to.x - segment.from.x) / (segment.length || 1);
    const ty = (segment.to.y - segment.from.y) / (segment.length || 1);
    samples.push({
      x: segment.from.x + (segment.to.x - segment.from.x) * ratio,
      y: segment.from.y + (segment.to.y - segment.from.y) * ratio,
      distance: travelled,
      tx,
      ty,
    });
  }
  const last = segments.at(-1)!;
  const lastSample = samples.at(-1);
  if (!lastSample || distance(lastSample, last.to) > 0.5) {
    samples.push({
      ...last.to,
      distance: total,
      tx: (last.to.x - last.from.x) / (last.length || 1),
      ty: (last.to.y - last.from.y) / (last.length || 1),
    });
  }
  return samples;
}

function svgPath(points: Point[]) {
  return points.length
    ? `M${points.map((point) => `${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join("L")}`
    : "";
}

function offsetSamples(samples: Sample[], offset: (sample: Sample) => number) {
  return samples.map((sample) => {
    const amount = offset(sample);
    return {
      x: sample.x - sample.ty * amount,
      y: sample.y + sample.tx * amount,
    };
  });
}

function sampleAtDistance(samples: Sample[], target: number): Sample {
  const last = samples.at(-1)!;
  const clamped = Math.max(0, Math.min(target, last.distance));
  const afterIndex = samples.findIndex((sample) => sample.distance >= clamped);
  if (afterIndex <= 0) return samples[0];
  const after = samples[afterIndex];
  const before = samples[afterIndex - 1];
  const span = after.distance - before.distance || 1;
  const ratio = (clamped - before.distance) / span;
  const tx = before.tx + (after.tx - before.tx) * ratio;
  const ty = before.ty + (after.ty - before.ty) * ratio;
  const tangentLength = Math.hypot(tx, ty) || 1;
  return {
    x: before.x + (after.x - before.x) * ratio,
    y: before.y + (after.y - before.y) * ratio,
    distance: clamped,
    tx: tx / tangentLength,
    ty: ty / tangentLength,
  };
}

function pointWithOffset(sample: Sample, amount: number): Point {
  return {
    x: sample.x - sample.ty * amount,
    y: sample.y + sample.tx * amount,
  };
}

function backwardStrideGeometry(samples: Sample[]) {
  const total = samples.at(-1)!.distance;
  const stride = 25;
  const gap = 7;
  const commands: string[] = [];
  const puckMarks: string[] = [];

  for (
    let start = 0, index = 0;
    start < total - 2;
    start += stride + gap, index += 1
  ) {
    const end = Math.min(start + stride, total);
    const length = end - start;
    if (length < 6) break;
    const side = index % 2 === 0 ? -1 : 1;
    const stridePoints = Array.from({ length: 9 }, (_, pointIndex) => {
      const progress = pointIndex / 8;
      const sample = sampleAtDistance(samples, start + length * progress);
      return pointWithOffset(sample, Math.sin(progress * Math.PI) * 6 * side);
    });
    commands.push(svgPath(stridePoints));

    const midpoint = sampleAtDistance(samples, start + length / 2);
    const puck = pointWithOffset(midpoint, side * 1.3);
    puckMarks.push(`M${puck.x.toFixed(2)} ${puck.y.toFixed(2)}l0.01 0.01`);
  }

  return { strides: commands.join(""), puckMarks: puckMarks.join("") };
}

export type RenderedPath = {
  primary: string;
  secondary?: string;
  ticks?: string;
  dash?: string;
};

export function renderPathGeometry(
  kind: PathKind,
  points: Point[],
): RenderedPath {
  if (kind === "straight") {
    const start = points[0];
    const end = points.at(-1);
    return {
      primary:
        start && end
          ? `M${start.x.toFixed(2)} ${start.y.toFixed(2)}L${end.x.toFixed(2)} ${end.y.toFixed(2)}`
          : "",
    };
  }
  const samples = samplePath(points);
  const primary = svgPath(samples);
  if (!samples.length) return { primary };

  if (kind === "skatePuck" || kind === "dropPass") {
    const total = samples.at(-1)!.distance;
    return {
      primary: svgPath(
        offsetSamples(samples, ({ distance: travelled }) => {
          const endFade = Math.min(1, travelled / 14, (total - travelled) / 18);
          return Math.sin((travelled / 24) * Math.PI * 2) * 5.5 * endFade;
        }),
      ),
    };
  }

  if (kind === "backward" || kind === "backwardPuck") {
    const backward = backwardStrideGeometry(samples);
    return {
      primary: backward.strides,
      secondary: kind === "backwardPuck" ? backward.puckMarks : undefined,
    };
  }

  if (kind === "lateral") {
    const total = samples.at(-1)!.distance;
    const tickPath = Array.from(
      { length: Math.floor(total / 13) + 1 },
      (_, index) => sampleAtDistance(samples, Math.min(index * 13, total)),
    )
      .map((sample) => {
        const half = 6.5;
        return `M${(sample.x - sample.ty * half).toFixed(2)} ${(sample.y + sample.tx * half).toFixed(2)}L${(sample.x + sample.ty * half).toFixed(2)} ${(sample.y - sample.tx * half).toFixed(2)}`;
      })
      .join("");
    return { primary: "", ticks: tickPath };
  }

  if (kind === "shot") {
    return {
      primary: svgPath(offsetSamples(samples, () => -2.5)),
      secondary: svgPath(offsetSamples(samples, () => 2.5)),
    };
  }

  return {
    primary,
    dash: kind === "pass" ? "2 11" : kind === "pressure" ? "20 6" : undefined,
  };
}
